import React, { useRef, useEffect, useState } from "react";

import FilterItem, {
  getFilterConditionOptions,
} from "./filter-item/filter-item";

import CustomScrollbar from "../../components/scrollbar/customScrollbar";
import styles from "./filter-sidebar.scss";

import Button from "../../components/button";
import _, { isEmpty, isNull, uniqueId } from "lodash";

import {
  ConvertInsightsFiltersToFilterSidebar,
  hiddenFilter,
} from "./filter-conversions";
import { deepClone } from "../../utils/other/utilities";
import { UpdateFilterBySubjectTo } from "../../utils/filters/filtersUtils";

const FilterCollection = ({
  filtersCollection,
  setFiltersCollection,
  onFiltersChangeImmediate,
  insightsFiltersCollection,
  filterDefinitions,
  filterDefinitionsHaveLoaded,
  scrollable = true,
  height,
  viewTypeId, // ⚠️ CHANGE 1: Added viewTypeId prop
}) => {
  const [shouldOpenNewFilter, setShouldOpenNewFilter] = useState(false);

  const [
    anchoredFiltersDefinitions,
    nonAnchoredFiltersDefinitions,
  ] = filterDefinitions?.reduce(
    ([anchoredFilters, nonAnchoredFilters], filter) => {
      if (filter.constraints?.Bounded)
        return [anchoredFilters, nonAnchoredFilters];

      return filter.constraints?.SelectedByDefault
        ? [[...anchoredFilters, filter], nonAnchoredFilters]
        : [anchoredFilters, [filter, ...nonAnchoredFilters]];
    },
    [[], []]
  );

  const [
    UpdatedFiltersAfterSelection,
    setUpdatedFiltersAfterSelection,
  ] = useState([]);

  useEffect(() => {
    setUpdatedFiltersAfterSelection(
      deepClone(filterDefinitions).map((filter) =>
        UpdateFilterBySubjectTo(filter, filtersCollection)
      )
    );
  }, [filtersCollection, filterDefinitions]);

  useEffect(() => {
    // fill values for all filters
    if (filterDefinitionsHaveLoaded) {
      filtersCollection
        .filter((x) => isNull(x.values))
        .forEach((filter) => {
          const filterDefinition = filterDefinitions.find(
            (x) => x.mbName === filter.mbName
          );
          filter.values = filterDefinition.values;
        });
    }
  }, [filterDefinitionsHaveLoaded]);

  const onAddFilter = () => {
    const id = uniqueId();
    filtersCollection.unshift({ id: id });
    const updatedCollection = [...filtersCollection];
    setFiltersCollection(updatedCollection);
    
    setShouldOpenNewFilter(id);
  };

  const onDisposeFilter = (id) => {
    const index = filtersCollection.findIndex((x) => x.id === id);
    const removedFilter = filtersCollection[index];
    
    filtersCollection.splice(index, 1);
    const updatedCollection = [...filtersCollection];
    setFiltersCollection(updatedCollection);
    
    // ⚠️ CHANGE 2: Only notify Redux immediately if removing RETAILER on Brand Performance (view type 11)
    const isBrandPerformance = viewTypeId === 11;
    const isRetailer = 
      removedFilter?.displayName === 'Retailer' || 
      removedFilter?.mbName === 'rootdomain.keyword' ||
      removedFilter?.mbName === 'rootdomain';
    
    if (onFiltersChangeImmediate && isRetailer && isBrandPerformance) {
      onFiltersChangeImmediate(updatedCollection);
    }
    
    setShouldOpenNewFilter(false);
  };

  const clearFiltersOnChange = (filter) => {
    let updatedFiltersCollection = filtersCollection;
    if (filter.constraints?.ClearFiltersOnChange ?? false) {
      for (let filterName of filter.constraints.ClearFiltersOnChange) {
        const filterDefinition = filterDefinitions.find(
          (x) => x.mbName === filterName
        );
        updatedFiltersCollection = updatedFiltersCollection?.filter(
          (x) => x.mbName && x.mbName !== filterDefinition.mbName
        );
        for (let selectedFilter of updatedFiltersCollection) {
          selectedFilter.childFilters = selectedFilter.childFilters?.filter(
            (x) => x.mbName && x.mbName !== filterDefinition.mbName
          );
        }
      }
    }
    return updatedFiltersCollection;
  };

  const refreshDependentChildFilters = (collection) => {
    collection.forEach((filter) => {
      if (!filter.childFilters?.length) return;
      filter.childFilters = filter.childFilters.map((childFilter) => {
        // Always use the original un-narrowed definition from raw filterDefinitions
        // so that re-narrowing starts from the full values dict, not a stale snapshot.
        const originalDefinition = filterDefinitions.find(
          (x) => x.mbName === childFilter.mbName
        );
        if (!originalDefinition?.subjectTo?.length) return childFilter;

        // Re-narrow from the original full values using the current collection state
        const refreshed = UpdateFilterBySubjectTo(deepClone(originalDefinition), collection);
        const narrowedValues = refreshed.values;

        let newValues;
        let validOptions;
        if (originalDefinition.leafType === "CollectionDictionary") {
          // narrowedValues is a dict keyed by the top-level selection (e.g. { "TV": [...] }).
          // The child filter's `value` is the specific key chosen when the filter was added.
          newValues = narrowedValues?.[childFilter.value] ?? [];
          validOptions = Array.isArray(newValues) ? newValues : [];
        } else {
          // Collection / Compound: narrowedValues is the leaf array directly
          newValues = Array.isArray(narrowedValues) ? narrowedValues : [];
          validOptions = newValues;
        }

        // Keep selected properties that are still valid in the refreshed values
        let newSelectedProperties = childFilter.selectedProperties;
        if (newSelectedProperties != null) {
          if (Array.isArray(newSelectedProperties)) {
            newSelectedProperties = newSelectedProperties.filter((v) =>
              validOptions.includes(v)
            );
            if (newSelectedProperties.length === 0) newSelectedProperties = null;
          } else if (!validOptions.includes(newSelectedProperties)) {
            newSelectedProperties = null;
          }
        }

        return {
          ...childFilter,
          values: newValues,
          selectedProperties: newSelectedProperties,
        };
      });
    });
  };

  const onUpdateFilter = (id, data) => {
    const index = filtersCollection.findIndex((x) => x.id === id);
    const filter = isEmpty(data.displayName)
      ? {}
      : filterDefinitions.find((x) => x.displayName === data.displayName);
  
    // if filter !== null, user has selected a new filter. set condition to default by filter type
    const defaultCondition = isEmpty(filter)
      ? {}
      : { selectedCondition: getFilterConditionOptions(filter)[0].value };
  
    filtersCollection[index] = {
      ...filtersCollection[index],
      ...filter,
      ...defaultCondition,
      ...data,
    };

    // Refresh all child filters that have subjectTo dependencies
    refreshDependentChildFilters(filtersCollection);

    const filterDefinition = filterDefinitions.find(
      (x) => x.displayName === filtersCollection[index].displayName
    );
    const clearedFiltersCollection = clearFiltersOnChange(filterDefinition);
    setFiltersCollection([...clearedFiltersCollection]);
    
    // ⚠️ CHANGE 3: Only trigger immediate Redux update for RETAILER on Brand Performance (view type 11)
    const isBrandPerformance = viewTypeId === 11;
    const updatedFilter = filtersCollection[index];
    const isRetailer = 
      updatedFilter.displayName === 'Retailer' || 
      updatedFilter.mbName === 'rootdomain.keyword' ||
      updatedFilter.mbName === 'rootdomain';
    
    const hasValues = updatedFilter.selectedValues?.length > 0 || updatedFilter.values?.length > 0;
    
    // Only call immediate update for Retailer on Brand Performance screen
    if (onFiltersChangeImmediate && isRetailer && hasValues && isBrandPerformance) {
      onFiltersChangeImmediate([...clearedFiltersCollection]);
    }
    
    setShouldOpenNewFilter(false);
  };
  

  useEffect(() => {
    const updatedFilters = deepClone(filterDefinitions).map((filter) =>
      UpdateFilterBySubjectTo(filter, insightsFiltersCollection)
    );

    setFiltersCollection(
      ConvertInsightsFiltersToFilterSidebar(
        insightsFiltersCollection,
        updatedFilters,
        anchoredFiltersDefinitions
      )
    );
  }, [filterDefinitions, insightsFiltersCollection]);

  const totalAnchoredFilters = anchoredFiltersDefinitions.length;
  const addFilterButtonActive =
    filterDefinitions &&
    filterDefinitions.length > 0 &&
    (filtersCollection.length === 0 ||
      filtersCollection.length === totalAnchoredFilters ||
      filtersCollection[0].displayName != null);

  const nonAnchoredFiltersCollection = filtersCollection
    .filter((item) => !hiddenFilter(item))
    .map((item) =>
      item.constraints?.SelectedByDefault ? null : (
        <FilterItem
          key={item.id}
          selectableFiltersDefinitions={_.sortBy(
            nonAnchoredFiltersDefinitions,
            "displayName"
          ).filter((x) => !hiddenFilter(x))}
          filterDefinitions={UpdatedFiltersAfterSelection}
          item={item}
          onDispose={onDisposeFilter}
          onUpdateFilter={onUpdateFilter}
          openByDefault={shouldOpenNewFilter === item.id}
        />
      )
    );

  const collectionContent = (
    <div>
      {filtersCollection
        ?.filter(
          (item) => item.constraints?.SelectedByDefault && !hiddenFilter(item)
        )
        ?.map((item) => (
          <FilterItem
            key={item.displayName}
            filterDefinitions={filterDefinitions}
            item={item}
            onUpdateFilter={onUpdateFilter}
            forceSelectedFilterDisplayName={item.displayName}
          />
        ))}
      <Button
        id={"addFilterBtn"}
        onClick={onAddFilter}
        text={"+ Add Filter"}
        active={addFilterButtonActive}
      />
      {nonAnchoredFiltersCollection}
    </div>
  );

  return scrollable ? (
    <CustomScrollbar
      className={styles.customScrollbar}
      styles={styles}
      style={{ height: height }}
    >
      {collectionContent}
    </CustomScrollbar>
  ) : (
    collectionContent
  );
};

export default FilterCollection;
